import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssetDto, BuildingDto, RoomDto } from "@klantpunt/shared";
import { api } from "../../lib/api";

export const buildingKey = (id: string) => ["building", id] as const;

export function useBuilding(id: string) {
  return useQuery({ queryKey: buildingKey(id), queryFn: () => api<BuildingDto>(`/buildings/${id}`) });
}

/** Bijwerken van de gecachte gebouwdata na een mutatie (zonder opnieuw te laden). */
export function useBuildingCache(id: string) {
  const qc = useQueryClient();
  const update = (fn: (b: BuildingDto) => BuildingDto) =>
    qc.setQueryData<BuildingDto>(buildingKey(id), (b) => (b ? fn(b) : b));
  return {
    set: (b: BuildingDto) => qc.setQueryData(buildingKey(id), b),
    refresh: () => qc.invalidateQueries({ queryKey: buildingKey(id) }),
    upsertRoom: (r: RoomDto) =>
      update((b) => ({
        ...b,
        rooms: [...b.rooms.filter((x) => x.id !== r.id), r].sort((a, c) => a.name.localeCompare(c.name, "nl")),
      })),
    removeRoom: (roomId: string) =>
      update((b) => ({
        ...b,
        rooms: b.rooms.filter((x) => x.id !== roomId),
        assets: b.assets.map((x) => (x.roomId === roomId ? { ...x, roomId: null } : x)),
      })),
    upsertAsset: (x: AssetDto) =>
      update((b) => ({
        ...b,
        // Op aanmaakvolgorde (zoals de server): bestaande blijft op zijn plek, nieuwe achteraan.
        assets: b.assets.some((y) => y.id === x.id) ? b.assets.map((y) => (y.id === x.id ? x : y)) : [...b.assets, x],
      })),
    patchAsset: (assetId: string, patch: Partial<AssetDto>) =>
      update((b) => ({ ...b, assets: b.assets.map((x) => (x.id === assetId ? { ...x, ...patch } : x)) })),
    removeAsset: (assetId: string) => update((b) => ({ ...b, assets: b.assets.filter((x) => x.id !== assetId) })),
  };
}
